import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

export interface AIEntityRef {
  type:
    | "resource"
    | "campaign"
    | "page"
    | "search_term"
    | "dashboard"
    | "goal"
    | "kpi";
  id: number;
  name: string;
  channel?: string;
}

export interface ParsedAIContent {
  text: string;
  entities: AIEntityRef[];
  suggestions: string[];
}

/**
 * Parse a `---context` JSON block from AI response and return
 * clean text + structured entity references.
 *
 * The AI outputs:
 * ```
 * Some markdown text...
 * ---context
 * {"resources": [...], "campaigns": [...], "pages": [...]}
 * ---
 * ```
 */
// The closing `---` is optional, because the model sometimes omits it. That
// makes the terminator "a `---` on its own line", not "a `---` at the end of
// the string": anchoring on the end swallows every character after a block
// that *is* terminated but has prose following it.
const SUGGESTIONS_BLOCK =
  /---suggestions[ \t]*\n([\s\S]*?)(?:\n---[ \t]*(?=\n|$)|$)/;
const CONTEXT_BLOCK = /---context[ \t]*\n([\s\S]*?)(?:\n---[ \t]*(?=\n|$)|$)/;

interface TrailerSplit {
  /** The block's raw contents, or `undefined` when there is no block. */
  body: string | undefined;
  /** Everything that was not the block, in order. */
  rest: string;
}

function splitTrailerBlock(content: string, pattern: RegExp): TrailerSplit {
  const match = pattern.exec(content);
  if (!match || match.index === undefined) {
    return { body: undefined, rest: content };
  }
  const before = content.slice(0, match.index).trimEnd();
  const after = content.slice(match.index + match[0].length).trim();
  return {
    body: match[1].trim(),
    rest: after ? `${before}\n\n${after}`.trim() : before,
  };
}

export function parseAIContent(content: string): ParsedAIContent {
  // Suggestions first: the block may appear either side of the context block.
  const { body: suggestionsBody, rest: withoutSuggestions } = splitTrailerBlock(
    content,
    SUGGESTIONS_BLOCK,
  );
  let suggestions: string[] = [];

  if (suggestionsBody !== undefined) {
    try {
      const parsed = JSON.parse(suggestionsBody);
      if (Array.isArray(parsed)) {
        suggestions = parsed.filter(
          (s): s is string => typeof s === "string" && s.length > 0,
        );
      }
    } catch {
      // JSON parse failed — skip suggestions
    }
  }

  const { body: contextBody, rest: text } = splitTrailerBlock(
    withoutSuggestions,
    CONTEXT_BLOCK,
  );

  if (contextBody === undefined) {
    return { text, entities: [], suggestions };
  }

  const entities: AIEntityRef[] = [];

  try {
    const parsed = JSON.parse(contextBody);

    if (Array.isArray(parsed.resources)) {
      for (const r of parsed.resources) {
        if (r.id && r.name) {
          entities.push({
            type: "resource",
            id: r.id,
            name: r.name,
            channel: r.channel,
          });
        }
      }
    }
    if (Array.isArray(parsed.campaigns)) {
      for (const c of parsed.campaigns) {
        if (c.id && c.name) {
          entities.push({
            type: "campaign",
            id: c.id,
            name: c.name,
            channel: c.channel,
          });
        }
      }
    }
    if (Array.isArray(parsed.pages)) {
      for (const p of parsed.pages) {
        if (p.id && p.url) {
          entities.push({
            type: "page",
            id: p.id,
            name: p.url,
          });
        }
      }
    }
    if (Array.isArray(parsed.dashboards)) {
      for (const d of parsed.dashboards) {
        if (d.id && d.name) {
          entities.push({
            type: "dashboard",
            id: d.id,
            name: d.name,
          });
        }
      }
    }
    if (Array.isArray(parsed.goals)) {
      for (const g of parsed.goals) {
        if (g.id && g.name) {
          entities.push({
            type: "goal",
            id: g.id,
            name: g.name,
          });
        }
      }
    }
    if (Array.isArray(parsed.kpis)) {
      for (const k of parsed.kpis) {
        if (k.id && k.name) {
          entities.push({
            type: "kpi",
            id: k.id,
            name: k.name,
          });
        }
      }
    }
  } catch {
    // JSON parse failed — just return text without entities
  }

  return { text, entities, suggestions };
}

const TABLE_LABEL_MAX_COLUMNS_CHARS = 60;

// Text and code spans only: answers often name metrics in code spans, which
// renderInlineAsText would drop.
function plainText(tokens: MarkdownIt.Token[]): string {
  return tokens
    .map((token) =>
      token.type === "text" || token.type === "code_inline"
        ? token.content
        : plainText(token.children ?? []),
    )
    .join("");
}

// Numbered, and named after the header row, so a screen reader's list of
// regions tells an answer's tables apart, even two with the same columns.
// The number counts within one render; `prefix` (e.g. "Answer 3") tells
// apart tables from different renders on the same page.
function tableLabel(
  tokens: MarkdownIt.Token[],
  tableOpen: number,
  n: number,
  prefix: string | undefined,
): string {
  const headers: string[] = [];
  for (
    let i = tableOpen + 1;
    i < tokens.length && tokens[i].type !== "thead_close";
    i++
  ) {
    if (tokens[i].type !== "inline") continue;
    const text = plainText(tokens[i].children ?? [])
      .replace(/\s+/g, " ")
      .trim();
    if (text) headers.push(text);
  }
  const name = prefix ? `${prefix}, table ${n}` : `Table ${n}`;
  if (headers.length === 0) return name;
  const columns = Array.from(headers.join(", "));
  const shown =
    columns.length > TABLE_LABEL_MAX_COLUMNS_CHARS
      ? `${columns
          .slice(0, TABLE_LABEL_MAX_COLUMNS_CHARS - 1)
          .join("")
          .trimEnd()}…`
      : columns.join("");
  return `${name}: ${shown}`;
}

function createMarkdown({ images }: { images: boolean }): MarkdownIt {
  const instance = new MarkdownIt({ linkify: true, breaks: true });

  // Open all links in a new tab
  const defaultLinkRender =
    instance.renderer.rules.link_open ||
    ((tokens, idx, options, _env, self) =>
      self.renderToken(tokens, idx, options));

  instance.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    tokens[idx].attrSet("target", "_blank");
    tokens[idx].attrSet("rel", "noopener noreferrer");
    return defaultLinkRender(tokens, idx, options, env, self);
  };

  // No <img> is ever emitted, so no request can start before sanitising;
  // the alt text stays readable in its place.
  if (!images) {
    instance.renderer.rules.image = (tokens, idx, options, env, self) =>
      instance.utils.escapeHtml(
        self.renderInlineAsText(tokens[idx].children ?? [], options, env),
      );
  }

  // A focusable wrapper lets the keyboard scroll a wide table: Safari never
  // focuses a scroll container on its own. Every one gets it, not only one
  // that overflows, because overflow changes with each panel resize. Text
  // re-rendered while it is read, like a streaming answer, gets tabindex -1:
  // v-html replaces its wrappers on every chunk, which would throw a keyboard
  // user's focus out of the table, and without the attribute Chrome and
  // Firefox give an overflowing wrapper a Tab stop of their own.
  instance.renderer.rules.table_open = (tokens, idx, options, env, self) => {
    const html = self.renderToken(tokens, idx, options);
    if (!env.wrapTables) return html;
    if (!env.focusableTables) {
      return `<div class="md-table-scroll" tabindex="-1">${html}`;
    }
    env.tableCount = (env.tableCount ?? 0) + 1;
    const label = instance.utils.escapeHtml(
      tableLabel(tokens, idx, env.tableCount, env.tableLabelPrefix),
    );
    return `<div class="md-table-scroll" tabindex="0" role="region" aria-label="${label}">${html}`;
  };
  instance.renderer.rules.table_close = (tokens, idx, options, env, self) => {
    const html = self.renderToken(tokens, idx, options);
    return env.wrapTables ? `${html}</div>` : html;
  };
  return instance;
}

const md = createMarkdown({ images: true });
const mdWithoutImages = createMarkdown({ images: false });

// images: false for third-party text (e.g. AI Visibility's engine answers),
// where an image is a request to a server the text's author chose.
// wrapTables puts each <table> in a `div.md-table-scroll` for the caller to
// make scroll. Scrolling the table itself takes `display: block`, which leaves
// its rows in a box only as wide as their content, so a narrow table's borders
// stop short of the width it was given. Opt-in: even unstyled, the wrapper
// stops prose zeroing the margin of a table that opens or ends the text, and
// AI Visibility's answer modal already scrolls its whole response.
// tableLabelPrefix leads each focusable table's region name, for a page that
// shows several renders' tables at once.
export function renderMarkdown(
  content: string,
  {
    images = true,
    wrapTables = false,
    focusableTables = true,
    tableLabelPrefix,
  }: {
    images?: boolean;
    wrapTables?: boolean;
    focusableTables?: boolean;
    tableLabelPrefix?: string;
  } = {},
): string {
  const html = (images ? md : mdWithoutImages).render(content, {
    wrapTables,
    focusableTables,
    tableLabelPrefix,
  });
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ["target"],
    // Kept as a second line of defence for raw HTML that slips through.
    ...(images ? {} : { FORBID_TAGS: ["img"] }),
  });
}

// Newlines collapse to spaces so multi-line AI content stays on a
// single preview line in list/card summaries — block elements would
// break the compact layout `renderMarkdown` is built for.
export function renderMarkdownInline(
  content: string,
  { images = true }: { images?: boolean } = {},
): string {
  const singleLine = content.replace(/[\r\n]+/g, " ").trim();
  const instance = images ? md : mdWithoutImages;
  return DOMPurify.sanitize(instance.renderInline(singleLine), {
    ADD_ATTR: ["target"],
    // Same second line of defence as renderMarkdown, for raw HTML that slips
    // through the parser.
    ...(images ? {} : { FORBID_TAGS: ["img"] }),
  });
}
